/*
 * cart-delivery
 *
 * Renders the cart drawer's delivery estimate.
 *
 * Two rules matter here:
 *
 * 1. The clock is the SHOP's, not the visitor's. All date maths runs against a
 *    fixed UTC offset (data-utc-offset), so a shopper in London is told the same
 *    cutoff as a shopper in Singapore. The base timestamp is stamped by the
 *    server into data-now; a visitor with a wrong device clock still gets the
 *    right answer, because only ELAPSED time is read from the device.
 *
 * 2. The drawer is destroyed and rebuilt by cart.js on every quantity change,
 *    add and remove. This element therefore holds no state worth preserving —
 *    it recomputes from the clock, so a rebuild produces the same answer.
 *
 * Public holidays are not modelled. The error is at most one day.
 */
(function () {
  const SECONDS_PER_DAY = 86400;
  const MAX_DAY_SEARCH = 21; // guard against a ship-day set that never matches

  class CartDelivery extends HTMLElement {
    connectedCallback() {
      this.base = parseInt(this.dataset.now, 10);
      this.utcOffset = (parseInt(this.dataset.utcOffset, 10) || 0) * 3600;
      this.cutoff = this.parseCutoff(this.dataset.cutoff);
      this.shipDays = String(this.dataset.shipDays || '')
        .split('')
        .map((flag) => flag === '1');
      this.minDays = Math.max(0, parseInt(this.dataset.minDays, 10) || 0);
      this.maxDays = Math.max(this.minDays, parseInt(this.dataset.maxDays, 10) || 0);

      // Anything missing means we cannot make an honest promise. Say nothing.
      if (
        !Number.isFinite(this.base) ||
        this.cutoff === null ||
        this.shipDays.length !== 7 ||
        this.shipDays.indexOf(true) === -1
      ) {
        return;
      }

      /* A per-second tick is only worth its repaints while the seconds are
         actually on screen, which is only inside the countdown window. Outside
         it a quarter-minute keeps the minutes honest for a fraction of the
         work, so the timer reschedules itself off what was last drawn rather
         than running hot all day. */
      this.ticking = this.hasAttribute('data-tick');

      const countdownWithin = parseInt(this.dataset.countdownWithin, 10);
      this.countdownWithin = isFinite(countdownWithin) ? countdownWithin : Infinity;

      this.startedAt = this.monotonic();
      this.tick();
    }

    tick() {
      this.render();
      this.timer = setTimeout(
        () => this.tick(),
        this.ticking && this.inCountdown ? 1000 : 15000
      );
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
    }

    monotonic() {
      return typeof performance !== 'undefined' && performance.now
        ? performance.now()
        : Date.now();
    }

    /* "14:00" -> 50400 seconds past midnight. Invalid input -> null. */
    parseCutoff(value) {
      const match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(String(value || ''));
      if (!match) return null;
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      if (hours > 23 || minutes > 59) return null;
      return hours * 3600 + minutes * 60;
    }

    /* Seconds since epoch, server-based, advanced by elapsed device time. */
    nowSeconds() {
      return this.base + Math.floor((this.monotonic() - this.startedAt) / 1000);
    }

    /*
     * A Date whose UTC fields read as the shop's wall clock. Every getter and
     * constructor below is UTC-based so the visitor's timezone never leaks in.
     */
    shopDate(epochSeconds) {
      return new Date((epochSeconds + this.utcOffset) * 1000);
    }

    startOfDay(date) {
      return new Date(
        Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
      );
    }

    addDays(date, days) {
      return new Date(date.getTime() + days * SECONDS_PER_DAY * 1000);
    }

    isShipDay(date) {
      return this.shipDays[date.getUTCDay()] === true;
    }

    nextShipDay(date) {
      let candidate = date;
      for (let i = 0; i < MAX_DAY_SEARCH; i += 1) {
        if (this.isShipDay(candidate)) return candidate;
        candidate = this.addDays(candidate, 1);
      }
      return null;
    }

    /* Advance `count` working days, counting only configured shipping days. */
    addWorkingDays(date, count) {
      let cursor = date;
      for (let i = 0; i < count; i += 1) {
        cursor = this.nextShipDay(this.addDays(cursor, 1));
        if (!cursor) return null;
      }
      return cursor;
    }

    formatDate(date) {
      const locale = document.documentElement.lang || 'en';
      return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
      }).format(date);
    }

    formatTimeLeft(seconds) {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      /* Seconds only where a template asked for them, so the countdown reads as
         a clock running rather than a number that changes when you look away. */
      if (this.ticking && this.dataset.hoursMinutesSeconds) {
        const secs = String(Math.floor(seconds % 60)).padStart(2, '0');
        // Under an hour, '0h 30m 00s' reads as a broken clock. Drop the slot.
        if (hours === 0 && this.dataset.minutesSeconds) {
          return String(this.dataset.minutesSeconds)
            .replace('[m]', minutes)
            .replace('[s]', secs);
        }
        return String(this.dataset.hoursMinutesSeconds)
          .replace('[h]', hours)
          .replace('[m]', String(minutes).padStart(2, '0'))
          .replace('[s]', secs);
      }

      if (hours > 0) {
        return String(this.dataset.hoursMinutes || '[h]h [m]m')
          .replace('[h]', hours)
          .replace('[m]', minutes);
      }
      // Under a minute still reads as "1m" rather than "0m" — never say zero.
      return String(this.dataset.minutes || '[m]m').replace(
        '[m]',
        Math.max(1, minutes)
      );
    }

    render() {
      const epoch = this.nowSeconds();
      const shopNow = this.shopDate(epoch);
      const today = this.startOfDay(shopNow);
      const secondsToday =
        shopNow.getUTCHours() * 3600 +
        shopNow.getUTCMinutes() * 60 +
        shopNow.getUTCSeconds();

      const dispatchesToday = this.isShipDay(today) && secondsToday < this.cutoff;
      const dispatch = dispatchesToday
        ? today
        : this.nextShipDay(this.addDays(today, 1));
      if (!dispatch) return;

      const earliest = this.addWorkingDays(dispatch, this.minDays);
      const latest = this.addWorkingDays(dispatch, this.maxDays);
      if (!earliest || !latest) return;

      const range =
        earliest.getTime() === latest.getTime()
          ? this.formatDate(earliest)
          : this.formatDate(earliest) +
            (this.dataset.rangeSeparator || ' – ') +
            this.formatDate(latest);

      /* A countdown reading thirteen hours reassures where it means to press,
         so the clock only comes out once the cutoff is genuinely close. The
         rest of the day states the rule instead, which is the thing a shopper
         can actually act on. */
      const timeLeft = this.cutoff - secondsToday;
      const before = this.dataset.templateBefore;
      this.inCountdown =
        dispatchesToday && (!before || timeLeft <= this.countdownWithin);

      let markup;
      if (this.inCountdown) {
        markup = String(this.dataset.templateCountdown || '')
          .replace('[date_range]', range)
          .replace('[time_left]', this.formatTimeLeft(timeLeft));
      } else if (dispatchesToday) {
        markup = String(before)
          .replace('[date_range]', range)
          .replace('[cutoff_time]', this.dataset.cutoffLabel || '');
      } else {
        markup = String(this.dataset.template || '').replace('[date_range]', range);
      }

      this.innerHTML = markup;
      this.setAttribute('data-ready', '');
    }
  }

  if (!customElements.get('cart-delivery')) {
    customElements.define('cart-delivery', CartDelivery);
  }
})();
